<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSend', 'cancel', 'update:show']);

const { t } = useI18n();

const query = ref('');
const flows = ref([]);
const isLoading = ref(false);
const selectedFlow = ref(null);
const form = ref({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttonText: '',
});

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const publishedFlows = computed(() => {
  return flows.value.filter(flow =>
    ['published', 'publicado'].includes(
      (flow.status || '').toString().toLowerCase()
    )
  );
});

const filteredFlows = computed(() => {
  return publishedFlows.value.filter(flow =>
    (flow.name || '').toLowerCase().includes(query.value.toLowerCase())
  );
});

const canSend = computed(() => {
  return selectedFlow.value && form.value.bodyText.trim() && form.value.buttonText.trim();
});

const modalHeaderContent = computed(() => {
  return selectedFlow.value
    ? t('WHATSAPP_FLOWS.MODAL.FLOW_SELECTED_SUBTITLE', {
        flowName: selectedFlow.value.name,
      })
    : t('WHATSAPP_FLOWS.MODAL.SUBTITLE');
});

const resetSelection = () => {
  selectedFlow.value = null;
  form.value = {
    headerText: '',
    bodyText: '',
    footerText: '',
    buttonText: '',
  };
};

const loadFlows = async () => {
  if (!props.inboxId) return;

  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppFlows(props.inboxId);
    flows.value = response.data?.payload || [];
  } catch (error) {
    useAlert(t('WHATSAPP_FLOWS.PICKER.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const pickFlow = flow => {
  selectedFlow.value = flow;
  form.value.buttonText = flow.flow_cta || t('WHATSAPP_FLOWS.PICKER.DEFAULT_CTA');
  form.value.bodyText = flow.description || flow.name || '';
};

const sendFlow = () => {
  if (!canSend.value) return;

  emit('onSend', {
    message: form.value.bodyText,
    contentAttributes: {
      whatsappFlow: {
        id: selectedFlow.value.id,
        name: selectedFlow.value.name,
      },
    },
    templateParams: {
      message_type: 'flow',
      flow_id: selectedFlow.value.id,
      flow_name: selectedFlow.value.name,
      flow_cta: form.value.buttonText,
      header_text: form.value.headerText,
      body_text: form.value.bodyText,
      footer_text: form.value.footerText,
      flow_action: 'navigate',
      flow_action_payload: {
        screen: 'FIRST_ENTRY_SCREEN',
      },
    },
  });
};

const onClose = () => {
  emit('cancel');
  resetSelection();
};

watch(
  () => props.show,
  value => {
    if (value) {
      loadFlows();
      return;
    }

    resetSelection();
    query.value = '';
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="$t('WHATSAPP_FLOWS.MODAL.TITLE')"
      :header-content="modalHeaderContent"
    />
    <div class="row modal-content">
      <div v-if="!selectedFlow" class="w-full">
        <div class="flex gap-2 mb-2.5">
          <div
            class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus-within:outline-n-brand"
          >
            <fluent-icon icon="search" class="text-n-slate-12" size="16" />
            <input
              v-model="query"
              type="search"
              :placeholder="$t('WHATSAPP_FLOWS.PICKER.SEARCH_PLACEHOLDER')"
              class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
            />
          </div>
          <button
            :disabled="isLoading"
            class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :title="$t('WHATSAPP_FLOWS.PICKER.REFRESH_BUTTON')"
            @click="loadFlows"
          >
            <fluent-icon
              icon="arrow-clockwise"
              class="text-n-slate-12"
              size="16"
              :class="{ 'animate-spin': isLoading }"
            />
          </button>
        </div>

        <div
          class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[18.75rem] overflow-y-auto p-2.5"
        >
          <div v-if="isLoading" class="py-8 text-center text-n-slate-11">
            {{ $t('WHATSAPP_FLOWS.PICKER.LOADING') }}
          </div>
          <template v-else>
            <div v-for="(flow, i) in filteredFlows" :key="flow.id">
              <button
                class="block p-2.5 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2"
                @click="pickFlow(flow)"
              >
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <p class="text-sm mb-1">{{ flow.name }}</p>
                    <p class="text-xs text-n-slate-11 mb-0">
                      {{ (flow.categories || []).join(', ') || 'OTHER' }}
                    </p>
                  </div>
                  <span
                    class="inline-block px-2 py-1 text-xs leading-none rounded-lg cursor-default bg-n-slate-3 text-n-slate-12"
                  >
                    {{ flow.status || 'DRAFT' }}
                  </span>
                </div>
              </button>
              <hr
                v-if="i !== filteredFlows.length - 1"
                :key="`hr-${flow.id}`"
                class="border-b border-solid border-n-weak my-2.5 mx-auto max-w-[95%]"
              />
            </div>

            <div v-if="!filteredFlows.length" class="py-8 text-center">
              <p class="text-n-slate-11">
                {{
                  query
                    ? $t('WHATSAPP_FLOWS.PICKER.NO_FLOWS_FOUND')
                    : $t('WHATSAPP_FLOWS.PICKER.NO_FLOWS_AVAILABLE')
                }}
              </p>
            </div>
          </template>
        </div>
      </div>

      <div v-else class="w-full">
        <div class="grid gap-4">
          <woot-input
            v-model="form.headerText"
            :label="$t('WHATSAPP_FLOWS.FORM.HEADER_LABEL')"
            :placeholder="$t('WHATSAPP_FLOWS.FORM.HEADER_PLACEHOLDER')"
          />
          <woot-input
            v-model="form.bodyText"
            :label="$t('WHATSAPP_FLOWS.FORM.BODY_LABEL')"
            :placeholder="$t('WHATSAPP_FLOWS.FORM.BODY_PLACEHOLDER')"
          />
          <woot-input
            v-model="form.footerText"
            :label="$t('WHATSAPP_FLOWS.FORM.FOOTER_LABEL')"
            :placeholder="$t('WHATSAPP_FLOWS.FORM.FOOTER_PLACEHOLDER')"
          />
          <woot-input
            v-model="form.buttonText"
            :label="$t('WHATSAPP_FLOWS.FORM.CTA_LABEL')"
            :placeholder="$t('WHATSAPP_FLOWS.FORM.CTA_PLACEHOLDER')"
          />
        </div>

        <footer class="flex gap-2 justify-end mt-6">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('WHATSAPP_FLOWS.FORM.GO_BACK_LABEL')"
            @click="resetSelection"
          />
          <NextButton
            type="button"
            :label="$t('WHATSAPP_FLOWS.FORM.SEND_MESSAGE_LABEL')"
            :disabled="!canSend"
            @click="sendFlow"
          />
        </footer>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.modal-content {
  padding: 1.5625rem 2rem;
}
</style>
